import type {To} from '@github-ui/react-router'
import {FocusKeys} from '@primer/behaviors'
import {useFocusZone} from '@primer/react'
import {clsx} from 'clsx'
import {type ComponentPropsWithoutRef, type ElementType, type RefObject, useCallback, useRef} from 'react'

import styles from './view-tab-nav.module.css'

const isTab = (element: HTMLElement) => element.getAttribute('role') === 'tab'

type TabNavProps = ComponentPropsWithoutRef<'div'>

function TabNav({'aria-label': ariaLabel, children, ...rest}: TabNavProps) {
  const containerRef = useRef<HTMLDivElement>(null)

  const focusSelectedTab = useCallback(() => {
    const selectedTab = containerRef.current?.querySelector<HTMLElement>('[role=tab][aria-selected=true]')
    const firstTab = containerRef.current?.querySelector<HTMLElement>('[role=tab]')
    return selectedTab ?? firstTab ?? undefined
  }, [])

  const {containerRef: navRef} = useFocusZone({
    containerRef,
    bindKeys: FocusKeys.ArrowHorizontal | FocusKeys.HomeAndEnd,
    focusOutBehavior: 'wrap',
    focusInStrategy: focusSelectedTab,
    focusableElementFilter: isTab,
  })

  return (
    <div {...rest} ref={navRef as RefObject<HTMLDivElement>} data-component="TabNav">
      <nav aria-label={ariaLabel} className={styles.tabNav}>
        <div role="tablist" className={styles.tabList}>
          {children}
        </div>
      </nav>
    </div>
  )
}

type TabNavLinkProps = React.DetailedHTMLProps<React.HTMLAttributes<HTMLAnchorElement>, HTMLAnchorElement> & {
  to?: To
  selected?: boolean
  className?: string
  as?: ElementType
  ref?: React.Ref<HTMLAnchorElement>
}

function TabNavLink({as, selected, className, ...rest}: TabNavLinkProps) {
  const Component = as ?? 'a'

  const linkClassName = clsx(
    'TabNav-item',
    styles.tabNavLink,
    selected && 'selected',
    selected && styles.selected,
    className,
  )

  return (
    <Component
      role="tab"
      tabIndex={-1}
      aria-selected={selected ? true : undefined}
      className={linkClassName}
      {...rest}
      data-component="TabNav.Link"
    />
  )
}

TabNav.Link = TabNavLink

export {TabNav}
